import { Reducer } from 'react';
import { defaultJobDetails } from './config';
import { IWebsocketData } from '../SharedComponents/types';
import { FileWithPath } from 'react-dropzone';

export interface AIStudioState {
  open: boolean;
  currentStep: number;
  optionalInputType: string;
  welcomeHeader: string;
  showCaret: boolean;
  buttonHovered: boolean;
  jobDetails: typeof defaultJobDetails;
  generatedTone: string;
  sendEmail: boolean;
  generatedResponse: string;
  generationError: boolean;
  questionType: string;
  generationType: string;
  navbarIsCollapsed: boolean;
  confirmCancelIsOpen: boolean;
  notificationData: IWebsocketData[];
  notificationModalOpen: boolean;
  aiStudioFrenchEnabled: boolean;
  document?: FileWithPath;
  refinedResponse: string;
  isLoading: boolean;
  questionHistory: string[];
  newQuestions: string;
  hasFeedbackInput: boolean;
}

export const initialAIStudioState: AIStudioState = {
  open: false,
  currentStep: 0,
  optionalInputType: '',
  welcomeHeader: '',
  showCaret: true,
  buttonHovered: false,
  jobDetails: defaultJobDetails,
  generatedTone: 'neutral',
  sendEmail: false,
  generatedResponse: '',
  generationError: false,
  questionType: 'general',
  generationType: 'description',
  navbarIsCollapsed: false,
  confirmCancelIsOpen: false,
  notificationData: [],
  notificationModalOpen: false,
  aiStudioFrenchEnabled: false,
  document: undefined,
  refinedResponse: '',
  isLoading: false,
  questionHistory: [],
  newQuestions: '',
  hasFeedbackInput: false
};

export type AIStudioAction =
  | { type: 'CLEAR_ALL_JOB_DETAILS' }
  | {
      type: 'INIT_NEW_JOB_FLOW';
      payload: {
        title: string;
        country: string;
      };
    }
  | { type: 'OPEN_MODAL' }
  | { type: 'CLOSE_MODAL' }
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'GO_TO_LOADING_STEP' }
  | { type: 'GO_TO_ERROR_STEP' }
  | { type: 'GO_TO_HOME_STEP' }
  | { type: 'SELECT_WRITE_TLP' }
  | { type: 'SELECT_WRITE_SCREENING_QUESTION' }
  | { type: 'SELECT_OPTIONAL_INPUT'; payload: string }
  | { type: 'ADD_TO_WELCOME_HEADER'; payload: string }
  | { type: 'CLEAR_WELCOME_HEADER' }
  | { type: 'SET_SHOW_CARET'; payload: boolean }
  | { type: 'SET_BUTTON_HOVERED'; payload: boolean }
  | { type: 'SET_JOB_DETAILS'; payload: string; fieldName: string }
  | { type: 'SET_GENERATED_TONE'; payload: string }
  | { type: 'SET_SEND_EMAIL'; payload: boolean }
  | { type: 'SET_GENERATED_RESPONSE'; payload: string; storeHistory?: boolean }
  | { type: 'SET_GENERATION_ERROR'; payload: boolean }
  | { type: 'SET_QUESTION_TYPE'; payload: string }
  | { type: 'SET_NAVBAR_IS_COLLAPSED'; payload: boolean }
  | { type: 'SET_CONFIRM_CANCEL_IS_OPEN'; payload: boolean }
  | { type: 'SET_NOTIFICATIONS'; payload: IWebsocketData[] }
  | { type: 'SET_RECEIVED_NOTIFICATION'; payload: IWebsocketData }
  | { type: 'DISMISS_NOTIFICATION' }
  | { type: 'VIEW_NOTIFICATION' }
  | { type: 'SET_DISMISSED_NOTIFICATION'; payload: number }
  | { type: 'TOGGLE_FRENCH' }
  | { type: 'SET_DOCUMENT'; payload: FileWithPath }
  | { type: 'SET_REFINED_RESPONSE'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | {
      type: 'SET_LOADING_AND_STEP';
      payload: { isLoading: boolean; step?: number; hasFeedbackInput?: boolean };
    }
  | { type: 'SET_NEW_QUESTIONS'; payload: string };

export const aiStudioReducer: Reducer<AIStudioState, AIStudioAction> = (
  state: AIStudioState,
  action: AIStudioAction
) => {
  switch (action.type) {
    case 'CLEAR_ALL_JOB_DETAILS':
      return {
        ...state,
        jobDetails: defaultJobDetails,
        generatedTone: 'neutral',
        sendEmail: false
      };
    case 'INIT_NEW_JOB_FLOW':
      return {
        ...state,
        jobDetails: {
          ...defaultJobDetails,
          position_title: action.payload.title,
          position_location: action.payload.country
        },
        currentStep: 1,
        generationType: 'description'
      };
    case 'CLOSE_MODAL':
      return initialAIStudioState;
    case 'OPEN_MODAL':
      return { ...state, open: true };
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'GO_TO_LOADING_STEP':
      return { ...state, currentStep: 3 };
    case 'GO_TO_ERROR_STEP':
      return { ...state, currentStep: 4 };
    case 'GO_TO_HOME_STEP':
      return { ...state, currentStep: 0 };
    case 'SELECT_WRITE_TLP':
      return { ...state, currentStep: 1, generationType: 'description' };
    case 'SELECT_WRITE_SCREENING_QUESTION':
      return { ...state, currentStep: 5, generationType: 'questions' };
    case 'SELECT_OPTIONAL_INPUT':
      return { ...state, optionalInputType: action.payload, currentStep: 2 };
    case 'ADD_TO_WELCOME_HEADER':
      return { ...state, welcomeHeader: state.welcomeHeader + action.payload };
    case 'CLEAR_WELCOME_HEADER':
      return { ...state, welcomeHeader: '' };
    case 'SET_SHOW_CARET':
      return { ...state, showCaret: action.payload };
    case 'SET_BUTTON_HOVERED':
      return { ...state, buttonHovered: action.payload };
    case 'SET_DOCUMENT':
      return { ...state, document: action.payload };
    case 'SET_JOB_DETAILS':
      return {
        ...state,
        jobDetails: {
          ...state.jobDetails,
          [action.fieldName]: action.payload
        }
      };
    case 'SET_GENERATED_TONE':
      return { ...state, generatedTone: action.payload };
    case 'SET_SEND_EMAIL':
      return { ...state, sendEmail: action.payload };
    case 'SET_GENERATED_RESPONSE':
      return {
        ...state,
        generatedResponse: action.payload,
        questionHistory:
          action.storeHistory && state.generatedResponse
            ? [...state.questionHistory, state.generatedResponse]
            : state.questionHistory
      };
    case 'SET_GENERATION_ERROR':
      return { ...state, generationError: action.payload };
    case 'SET_QUESTION_TYPE':
      return { ...state, questionType: action.payload };
    case 'SET_NAVBAR_IS_COLLAPSED':
      return { ...state, navbarIsCollapsed: action.payload };
    case 'SET_CONFIRM_CANCEL_IS_OPEN':
      return { ...state, confirmCancelIsOpen: action.payload };
    case 'SET_NOTIFICATIONS':
      return { ...state, notificationData: action.payload };
    case 'SET_RECEIVED_NOTIFICATION':
      if (!state.notificationModalOpen) {
        return {
          ...state,
          notificationData: [...state.notificationData, action.payload]
        };
      } else {
        const tempNotificationData = [...state.notificationData];
        tempNotificationData.splice(1, 0, action.payload);
        return {
          ...state,
          notificationData: tempNotificationData
        };
      }
    case 'DISMISS_NOTIFICATION':
      return {
        ...state,
        notificationData: state.notificationData.slice(1),
        notificationModalOpen: false
      };
    case 'VIEW_NOTIFICATION':
      return { ...state, notificationModalOpen: true };
    case 'SET_DISMISSED_NOTIFICATION':
      if (state.notificationModalOpen) return state;
      return {
        ...state,
        notificationData: state.notificationData.filter(
          (notification) => notification.id !== action.payload
        )
      };
    case 'TOGGLE_FRENCH':
      return { ...state, aiStudioFrenchEnabled: !state.aiStudioFrenchEnabled };
    case 'SET_REFINED_RESPONSE':
      return { ...state, refinedResponse: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_LOADING_AND_STEP':
      return {
        ...state,
        isLoading: action.payload.isLoading,
        ...(action.payload.step !== undefined && { currentStep: action.payload.step }),
        ...(action.payload.hasFeedbackInput !== undefined && {
          hasFeedbackInput: action.payload.hasFeedbackInput
        })
      };
    case 'SET_NEW_QUESTIONS':
      return {
        ...state,
        newQuestions: action.payload
      };
    default:
      return state;
  }
};
